import type { MutableRefObject } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import { productsApi } from '../../../api/endpoints/products';
import { ordersApi } from '../../../api/endpoints/orders';
import { customersApi } from '../../../api/endpoints/customers';
import { analyticsApi } from '../../../api/endpoints/analytics';
import { categoriesApi } from '../../../api/endpoints/categories';
import { settingsApi } from '../../../api/endpoints/settings';
import { sectionsApi, type PageSection } from '../../../api/endpoints/sections';
import { couponsApi } from '../../../api/endpoints/coupons';
import { cartsApi } from '../../../api/endpoints/carts';
import { inventoryApi } from '../../../api/endpoints/inventory';
import type { ChatToolSchema } from '../../../api/endpoints/ai';
import type { ProductFormHandle } from '../../../contexts/ProductFormContext';
import type { CouponFormHandle } from '../../../contexts/CouponFormContext';
import type { PaylinkFormHandle } from '../../../contexts/PaylinkFormContext';
import type { ImagePickerRequest, ImagePickerResult } from '../../../contexts/ImagePickerContext';

export interface ToolContext {
  storeId: string;
  navigate: NavigateFunction;
  productForm: {
    openCreate: () => void;
    openEdit: (id: string) => void;
    formRef: MutableRefObject<ProductFormHandle | null>;
  };
  couponForm: {
    openCreate: () => void;
    formRef: MutableRefObject<CouponFormHandle | null>;
  };
  paylinkForm: {
    openCreate: () => void;
    formRef: MutableRefObject<PaylinkFormHandle | null>;
  };
  imagePicker: {
    requestImageChoice: (req?: ImagePickerRequest) => Promise<ImagePickerResult>;
  };
  queryClient: QueryClient;
}

// Form-write tools may be invoked in the same turn as open_*; the modal needs
// a render tick to mount before useImperativeHandle fires.
async function waitForHandle<T>(ref: MutableRefObject<T | null>, timeoutMs = 600): Promise<T | null> {
  const step = 50;
  for (let waited = 0; waited < timeoutMs; waited += step) {
    if (ref.current) return ref.current;
    await new Promise((r) => setTimeout(r, step));
  }
  return ref.current;
}

async function waitForFormHandle(ctx: ToolContext, timeoutMs = 600): Promise<ProductFormHandle | null> {
  return waitForHandle(ctx.productForm.formRef, timeoutMs);
}

// Whitelist of safe routes the bot can send the user to. Keys are stable
// identifiers the model picks; values are template paths under /:storeId.
const ROUTE_MAP: Record<string, string> = {
  dashboard: '',
  products: 'products',
  orders: 'orders',
  customers: 'customers',
  categories: 'categories',
  coupons: 'coupons',
  coupons_create: 'coupons/create',
  paylinks: 'paylinks',
  paylinks_create: 'paylinks/create',
  reviews: 'reviews',
  analytics: 'analytics',
  search_analytics: 'analytics/search',
  live_visitors: 'analytics/live',
  shipping: 'shipping',
  inventory: 'inventory',
  carts: 'carts',
  marketing: 'marketing',
  newsletter: 'newsletter',
  blog: 'blog',
  contacts: 'contacts',
  complaints: 'complaints',
  apps: 'apps',
  wallet: 'wallet',
  expenses: 'expenses',
  settings: 'settings',
  settings_domain: 'settings?tab=domain',
  settings_payment_methods: 'settings?tab=paymentMethods',
  settings_notifications: 'settings?tab=notifications',
  settings_auth: 'settings?tab=auth',
  settings_api_keys: 'settings?tab=apiKeys',
  settings_webhooks: 'settings?tab=webhooks',
  settings_tracking: 'settings?tab=tracking',
  settings_apps: 'settings?tab=apps',
  design: 'design',
  design_pages: 'design?tab=pages',
  design_theme: 'design?tab=theme',
  design_navbar: 'design?tab=navbar',
  design_seo: 'design?tab=seo',
  design_languages: 'design?tab=languages',
  translations: 'translations',
  team: 'users',
  ab_testing: 'ab-testing',
  menus: 'menus',
  verification: 'verification',
};

export const TOOLS: ChatToolSchema[] = [
  {
    name: 'list_products',
    description:
      'Search and list products in the merchant store. Use when user asks about products by name, recent products, or product counts. Returns a compact list (id, title, price, quantity, status).',
    input_schema: {
      type: 'object',
      properties: {
        search: { type: 'string', description: 'Optional search query (matches title)' },
        limit: { type: 'number', description: 'Max products to return (default 10, max 25)' },
      },
    },
  },
  {
    name: 'get_product',
    description:
      'Fetch full details for one product by id. Returns price, variants, stock, plus pre-computed image metadata (description, dominant colors, detected objects, tags, style, mood, quality) for the preview image and any attachments. Use when the user asks anything specific about a product, including visual questions like "what color is this product" or "does this image look professional".',
    input_schema: {
      type: 'object',
      properties: {
        productId: { type: 'string', description: 'Product id (the productId field from list_products)' },
      },
      required: ['productId'],
    },
  },
  {
    name: 'list_orders',
    description:
      'List recent orders. Use when user asks about today\'s sales, recent orders, pending orders, etc.',
    input_schema: {
      type: 'object',
      properties: {
        status: {
          type: 'string',
          description: 'Optional delivery status filter (e.g. pending, processing, shipped, delivered)',
        },
        limit: { type: 'number', description: 'Max orders to return (default 10, max 25)' },
      },
    },
  },
  {
    name: 'list_customers',
    description:
      'List customers, optionally searched. Use when user asks about customers, top customers, etc.',
    input_schema: {
      type: 'object',
      properties: {
        search: { type: 'string', description: 'Optional search query (name, phone, email)' },
        limit: { type: 'number', description: 'Max customers to return (default 10, max 25)' },
      },
    },
  },
  {
    name: 'sales_summary',
    description:
      'Rich analytics snapshot for the merchant: stats (revenue, orders), top products, top customers, traffic (pageviews, unique visitors, bounce rate), traffic sources / referrers, device breakdown, country breakdown, conversion funnel (visit → cart → checkout → purchase), top pages, hourly visits, category breakdown, payment methods used, order status breakdown. Use this for any marketing or performance question. Period filters: today, yesterday, 7d, 30d, 90d, 12m, all. Default is 30d. PICK A PERIOD THAT MATCHES THE QUESTION: for "اليوم / today" use today; for "أمس / yesterday" use yesterday; for "best customer / best product / who/what is best" use 30d (or all-time if the user wants lifetime); for "this month" use 30d; for "this year" use 12m. Avoid defaulting to today for general "best X" questions — a quiet day will look like an empty store.',
    input_schema: {
      type: 'object',
      properties: {
        period: {
          type: 'string',
          description: 'Time window. One of: today, yesterday, 7d, 30d, 90d, 12m, all. Default: 30d.',
        },
      },
    },
  },
  {
    name: 'failed_searches',
    description:
      'List the store\'s most-searched terms that returned no results. Pure gold for marketing — failed searches are products customers wanted but the merchant didn\'t have. Use when the user asks about gaps, what to add to the catalog, or marketing opportunities.',
    input_schema: {
      type: 'object',
      properties: {
        days: { type: 'number', description: 'Trailing window in days (default 30)' },
        limit: { type: 'number', description: 'Max results (default 15)' },
      },
    },
  },
  {
    name: 'abandoned_carts_summary',
    description:
      'Summary of abandoned carts: count, total value left on the table, recent samples. Use when user asks about lost sales, cart recovery campaigns, or "what can I do about people not finishing checkout".',
    input_schema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'How many recent carts to sample (default 10)' },
      },
    },
  },
  {
    name: 'coupon_performance',
    description:
      'List coupons with their usage and revenue contribution. Tells the merchant which coupons drove real sales vs which sat unused.',
    input_schema: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max coupons to return (default 15)' },
        archived: { type: 'boolean', description: 'Include archived coupons (default false)' },
      },
    },
  },
  {
    name: 'low_stock_alerts',
    description:
      'Products that are low or out of stock. Use when user asks "what\'s running low", "what should I restock", or for proactive inventory advice.',
    input_schema: {
      type: 'object',
      properties: {
        filter: {
          type: 'string',
          description: 'One of: low (≤5 units), out (zero stock), all. Default: low.',
        },
        limit: { type: 'number', description: 'Max items (default 20)' },
      },
    },
  },
  {
    name: 'navigate_to',
    description:
      `Navigate the user to a dashboard page. Use when user asks "how do I do X" or "where can I see Y". Allowed routes: ${Object.keys(ROUTE_MAP).join(', ')}.`,
    input_schema: {
      type: 'object',
      properties: {
        route: {
          type: 'string',
          description: `One of: ${Object.keys(ROUTE_MAP).join(', ')}`,
        },
        reason: {
          type: 'string',
          description: 'Short Arabic explanation shown to the user about why we are navigating',
        },
      },
      required: ['route'],
    },
  },
  {
    name: 'open_product_create',
    description:
      'Open the "create product" modal so the user can add a new product (with variants, images, categories, etc.). Use when user says "add a product", "create a new product", etc.',
    input_schema: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short Arabic explanation shown to the user' },
      },
    },
  },
  {
    name: 'open_product_edit',
    description:
      'Open the "edit product" modal for a specific product. Use when user wants to edit an existing product, change its variants, price, images, etc. If you only know the product name, first call list_products to resolve the productId.',
    input_schema: {
      type: 'object',
      properties: {
        productId: { type: 'string', description: 'Product id from list_products' },
        reason: { type: 'string', description: 'Short Arabic explanation' },
      },
      required: ['productId'],
    },
  },
  {
    name: 'set_product_form_fields',
    description:
      'Patch fields on the currently-open product create/edit modal. Use after open_product_create or open_product_edit to fill in title, description, price, etc. for the user. Description accepts HTML (paragraphs, lists, bold). Only include fields you actually want to set.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Product title' },
        description: { type: 'string', description: 'Product description (HTML allowed)' },
        price: { type: 'number', description: 'Base price' },
        salePrice: { type: 'number', description: 'Sale price (optional, must be lower than price)' },
        compareAtPrice: { type: 'number', description: 'Compare-at price for showing a discount badge' },
        sku: { type: 'string', description: 'SKU code' },
        quantity: { type: 'number', description: 'Stock quantity. Sets unlimited=false implicitly.' },
        unlimitedQuantity: { type: 'boolean', description: 'Set true to mark as unlimited stock' },
        productType: {
          type: 'string',
          description: 'One of: physical, digital, service, code, file',
        },
        accessibility: {
          type: 'string',
          description: 'One of: public, private, password, archived',
        },
        weight: { type: 'number', description: 'Weight (for physical products)' },
      },
    },
  },
  {
    name: 'add_product_variant_type',
    description:
      'Add a new variant axis (e.g. Color, Size) with its options to the currently-open product modal. The form will auto-generate a variant for every combination of options across all axes. Call once per axis.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Axis name in Arabic or English (e.g. "اللون", "Size")' },
        options: {
          type: 'array',
          description: 'List of option labels, e.g. ["أحمر", "أسود", "أبيض"]',
        },
      },
      required: ['name', 'options'],
    },
  },
  {
    name: 'set_product_variant',
    description:
      'Override a single auto-generated variant\'s price, quantity, sku, or active flag on the currently-open product form. Identify the variant by its combination of option names across all axes — e.g. {"اللون": "أحمر", "المقاس": "L"}. Use after add_product_variant_type, or in edit mode after open_product_edit. Set price (absolute) OR priceModifier (delta from base), not both.',
    input_schema: {
      type: 'object',
      properties: {
        combination: {
          type: 'object',
          additionalProperties: { type: 'string' },
          description: 'Map of axis name to option name, e.g. {"اللون": "أحمر"}. Must include every axis that exists on the product.',
        },
        price: { type: 'number', description: 'Absolute price for this variant (overrides base price)' },
        priceModifier: { type: 'number', description: 'Delta added to the base price (e.g. +10 for "+10 SAR for size L")' },
        quantity: { type: 'number', description: 'Stock for this specific variant' },
        sku: { type: 'string', description: 'SKU code for this specific variant' },
        isActive: { type: 'boolean', description: 'Whether this combination is sellable' },
      },
      required: ['combination'],
    },
  },
  {
    name: 'list_categories',
    description:
      'List all categories in the store. Use this to resolve a category name to its categoryId before calling add_product_category.',
    input_schema: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_store_settings',
    description:
      'Read the merchant\'s store settings: connected social media links, tracking pixels (Facebook, Google Analytics, TikTok, Snapchat), enabled payment methods, languages, theme colors, SEO metadata, timezone. Use whenever the user asks about marketing setup, "هل الفيسبوك بكسل عندي شغال", connected accounts, payment methods, or branding.',
    input_schema: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'add_product_category',
    description:
      'Attach a category to the currently-open product modal. Resolve the categoryId via list_categories first if needed.',
    input_schema: {
      type: 'object',
      properties: {
        categoryId: { type: 'string', description: 'Category id (the _id from list_categories)' },
      },
      required: ['categoryId'],
    },
  },
  {
    name: 'open_coupon_create',
    description:
      'Open the "create coupon" modal so the user can issue a discount code. Use when the merchant says things like "create a 15% coupon" or "make a SAVE10 code". Pair with set_coupon_form_fields in the same turn to pre-fill.',
    input_schema: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short Arabic explanation shown to the user' },
      },
    },
  },
  {
    name: 'set_coupon_form_fields',
    description:
      'Patch fields on the currently-open coupon-create modal. Use after open_coupon_create. Dates are YYYY-MM-DD. Only include fields you actually want to set.',
    input_schema: {
      type: 'object',
      properties: {
        couponId: { type: 'string', description: 'Coupon code, e.g. "SAVE15"' },
        discountType: {
          type: 'string',
          description: 'One of: percentage, fixed',
        },
        amount: { type: 'number', description: 'Discount amount: percent (e.g. 15) for percentage, currency value for fixed' },
        maximumAmount: { type: 'number', description: 'Maximum discount cap (only for percentage type)' },
        minPurchase: { type: 'number', description: 'Minimum cart total to qualify' },
        maxUses: { type: 'number', description: 'Total redemptions allowed' },
        startDate: { type: 'string', description: 'Start date (YYYY-MM-DD)' },
        expiryDate: { type: 'string', description: 'Expiry date (YYYY-MM-DD)' },
        description: { type: 'string', description: 'Internal note for the merchant' },
        isActive: { type: 'boolean', description: 'Whether the coupon is active immediately' },
      },
    },
  },
  {
    name: 'open_paylink_create',
    description:
      'Open the "create paylink" modal. Use when the merchant says things like "create a payment link for 200 SAR" or "make a paylink for X". Pair with set_paylink_form_fields in the same turn to pre-fill the title and amount.',
    input_schema: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short Arabic explanation shown to the user' },
      },
    },
  },
  {
    name: 'set_paylink_form_fields',
    description:
      'Patch fields on the currently-open paylink-create modal. Use after open_paylink_create. Only include fields you want to set.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Visible label for the paylink (required for save)' },
        amount: { type: 'number', description: 'Charge amount (omit for "ask the buyer")' },
        currency: { type: 'string', description: 'Currency code (e.g. "SAR"). Omit to use store default.' },
        description: { type: 'string', description: 'Description shown to the buyer' },
      },
    },
  },
  {
    name: 'request_image_choice',
    description:
      'Show the merchant a picker to either select one of their recent images or upload a new one. Use whenever you need an image for something (banner, product preview, blog cover, etc). The tool blocks until the user picks or cancels. Return value tells you the chosen filename and url, or that the user cancelled.',
    input_schema: {
      type: 'object',
      properties: {
        purpose: {
          type: 'string',
          description: 'Short Arabic label for what the image is for, shown to the user (e.g. "صورة للبانر الرئيسي")',
        },
        hint: {
          type: 'string',
          description: 'Optional Arabic hint about preferred dimensions / style (e.g. "أفقية بعرض كامل")',
        },
      },
    },
  },
  {
    name: 'list_page_sections',
    description:
      'List the sections that compose a storefront page (banner, products, footer, etc) with their inputs, variant, and position. Always call this first before editing anything on a page so you know the current state and can identify sections by name+order. Defaults to the home page.',
    input_schema: {
      type: 'object',
      properties: {
        page: {
          type: 'string',
          description: 'Page slug. Common: "home", "product". Default: "home".',
        },
      },
    },
  },
  {
    name: 'update_section_inputs',
    description:
      'Update an existing section on a storefront page. Identify the target by name + order from list_page_sections. The "inputs" object you pass is shallow-merged into the section\'s existing inputs (top-level keys you don\'t pass are preserved; arrays you DO pass replace the existing array, so for nested edits like banners[0].image you must read the current banners array, modify it, and pass the full new array). Optionally swap variant or toggle hidden.',
    input_schema: {
      type: 'object',
      properties: {
        page: { type: 'string', description: 'Page slug (default "home")' },
        name: { type: 'string', description: 'Section name (e.g. "banner")' },
        order: { type: 'number', description: 'Current order of the target section, from list_page_sections' },
        inputs: {
          type: 'object',
          additionalProperties: true,
          description: 'Partial inputs to merge in. Top-level keys not present are preserved.',
        },
        variant: { type: 'string', description: 'Optional new variant id' },
        hidden: { type: 'boolean', description: 'Optional show/hide toggle' },
        className: { type: 'string', description: 'Optional Tailwind class string for the section wrapper' },
      },
      required: ['name', 'order'],
    },
  },
  {
    name: 'add_page_section',
    description:
      'Add a new section to a storefront page. position is the index where it should sit (0 = top); omit to append at the end. inputs may be left empty — the renderer tolerates missing fields.',
    input_schema: {
      type: 'object',
      properties: {
        page: { type: 'string', description: 'Page slug (default "home")' },
        name: { type: 'string', description: 'Section type (e.g. "banner", "cta", "features", "products")' },
        variant: { type: 'string', description: 'Optional variant id (default "default")' },
        inputs: {
          type: 'object',
          additionalProperties: true,
          description: 'Initial inputs for the new section. Empty object is fine.',
        },
        position: {
          type: 'number',
          description: '0-indexed position in the page; omit to append.',
        },
      },
      required: ['name'],
    },
  },
  {
    name: 'remove_page_section',
    description:
      'Remove a section from a storefront page. Identify the target by name + order from list_page_sections.',
    input_schema: {
      type: 'object',
      properties: {
        page: { type: 'string', description: 'Page slug (default "home")' },
        name: { type: 'string' },
        order: { type: 'number', description: 'Current order of the target section' },
      },
      required: ['name', 'order'],
    },
  },
  {
    name: 'move_section',
    description:
      'Reorder a section on a page. Pass its current order (from list_page_sections) and the new 0-indexed position. Other sections shift to make room. To swap two sections, call this twice (move A to B\'s slot, then move the displaced one to A\'s old slot — refresh between by re-listing).',
    input_schema: {
      type: 'object',
      properties: {
        page: { type: 'string', description: 'Page slug (default "home")' },
        name: { type: 'string' },
        order: { type: 'number', description: 'Current order' },
        newPosition: { type: 'number', description: 'Target 0-indexed position' },
      },
      required: ['name', 'order', 'newPosition'],
    },
  },
];

function cap(text: string, max = 3000): string {
  if (text.length <= max) return text;
  return text.slice(0, max) + `\n\n[... ${text.length - max} chars truncated]`;
}

function clampLimit(raw: unknown, def: number, max = 25): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return def;
  return Math.min(Math.floor(n), max);
}

// Normalize section order to 0..N-1 then whole-page save. The backend
// atomically replaces the active version's sections array, so we must send
// the full list (sending a partial would wipe the rest).
async function saveAndNormalize(storeId: string, page: string, sections: PageSection[]): Promise<void> {
  const normalized = sections.map((s, i) => ({ ...s, order: i }));
  await sectionsApi.save(storeId, page, normalized);
}

function pickProductFields(p: Record<string, any>) {
  return {
    id: p.productId || p._id,
    title: p.title,
    price: p.price,
    salePrice: p.salePrice,
    quantity: p.quantity,
    productType: p.productType,
    accessibility: p.accessibility,
    categories: Array.isArray(p.categories) ? p.categories.length : undefined,
    variants: Array.isArray(p.variants) ? p.variants.length : undefined,
  };
}

export async function dispatchTool(
  name: string,
  input: Record<string, unknown>,
  ctx: ToolContext,
): Promise<{ result: string; isError?: boolean }> {
  try {
    switch (name) {
      case 'list_products': {
        const search = typeof input.search === 'string' ? input.search : undefined;
        const limit = clampLimit(input.limit, 10);
        const data = await productsApi.list(ctx.storeId, { search, limit });
        const list = (data.products as Array<Record<string, any>>).slice(0, limit).map(pickProductFields);
        return {
          result: JSON.stringify({ total: data.total, returned: list.length, products: list }),
        };
      }

      case 'get_product': {
        const productId = String(input.productId || '');
        if (!productId) return { result: 'Missing productId', isError: true };
        const product = await productsApi.get(ctx.storeId, productId) as Record<string, any>;
        // Flatten attachment description map to an array (LLM-friendlier, drops the hash key).
        const attachmentDescs = product.attachmentDescriptions && typeof product.attachmentDescriptions === 'object'
          ? Object.values(product.attachmentDescriptions as Record<string, unknown>)
          : undefined;
        const compact = {
          id: product.productId || product._id,
          title: product.title,
          description: typeof product.description === 'string' ? product.description.slice(0, 500) : undefined,
          price: product.price,
          salePrice: product.salePrice,
          compareAtPrice: product.compareAtPrice,
          currency: product.currency,
          quantity: product.quantity,
          sku: product.sku,
          productType: product.productType,
          accessibility: product.accessibility,
          categories: product.categories,
          variantTypes: product.variantTypes,
          variants: Array.isArray(product.variants)
            ? product.variants.map((v: Record<string, any>) => ({
                title: v.title,
                price: v.price,
                quantity: v.quantity,
                sku: v.sku,
              }))
            : undefined,
          images: Array.isArray(product.images) ? product.images.length : 0,
          previewImageDescription: product.previewImageDescription || undefined,
          attachmentDescriptions: attachmentDescs && attachmentDescs.length > 0 ? attachmentDescs : undefined,
        };
        return { result: cap(JSON.stringify(compact)) };
      }

      case 'list_orders': {
        const status = typeof input.status === 'string' ? input.status : undefined;
        const limit = clampLimit(input.limit, 10);
        const data = await ordersApi.list(ctx.storeId, { status, limit } as Record<string, unknown>);
        const orders = (data.orders as Array<Record<string, any>>).slice(0, limit).map((o) => ({
          id: o.orderId || o._id,
          total: o.total,
          currency: o.currency,
          deliveryStatus: o.deliveryStatus,
          paymentStatus: o.paymentStatus,
          customerName: o.customer?.name || o.customerName,
          createdAt: o.createdAt,
          items: Array.isArray(o.items) ? o.items.length : undefined,
        }));
        return { result: cap(JSON.stringify({ total: (data as any).total, returned: orders.length, orders })) };
      }

      case 'list_customers': {
        const search = typeof input.search === 'string' ? input.search : undefined;
        const limit = clampLimit(input.limit, 10);
        const data = await customersApi.list(ctx.storeId, { search, limit } as Record<string, unknown>);
        const customers = ((data as any).customers as Array<Record<string, any>>).slice(0, limit).map((c) => ({
          id: c.customerId || c._id,
          name: c.name,
          email: c.email,
          phone: c.phone,
          ordersCount: c.ordersCount,
          totalSpent: c.totalSpent,
        }));
        return { result: cap(JSON.stringify({ returned: customers.length, customers })) };
      }

      case 'sales_summary': {
        // Default to 30d so "best customer / best product" style questions land
        // on a useful window. Today-specific questions pass period='today' explicitly.
        const period = typeof input.period === 'string' ? input.period : '30d';
        const data = await analyticsApi.getAnalytics(ctx.storeId, period) as Record<string, any>;
        const trim = <T,>(arr: T[] | undefined, n: number): T[] => Array.isArray(arr) ? arr.slice(0, n) : [];
        const compact: Record<string, unknown> = {
          period,
          stats: data.stats,
          traffic: data.traffic, // {pageViews, uniqueVisitors, bounceRate}
          activeVisitors: data.activeVisitors,
          last24hVisits: data.last24hVisits,
        };
        const topProducts = trim(data.topProducts as any[], 8).map((p) => ({
          title: p.title, sales: p.sales, revenue: p.revenue,
        }));
        const topCustomers = trim(data.topCustomers as any[], 5).map((c) => ({
          name: c.name, orders: c.orders, totalSpent: c.totalSpent,
        }));
        const trafficSources = trim(data.trafficSources as any[], 8).map((s) => ({
          source: s.source || s.name, visits: s.visits || s.count, conversions: s.conversions,
        }));
        const deviceBreakdown = trim(data.deviceBreakdown as any[], 6).map((d) => ({
          device: d.device || d.name, visits: d.visits || d.count, share: d.share || d.percentage,
        }));
        const countryBreakdown = trim(data.countryBreakdown as any[], 8).map((c) => ({
          country: c.country || c.name, visits: c.visits || c.count, revenue: c.revenue,
        }));
        const topPages = trim(data.topPages as any[], 8).map((p) => ({
          path: p.path || p.url, views: p.views || p.count,
        }));
        const categoryBreakdown = trim(data.categoryBreakdown as any[], 8).map((c) => ({
          name: c.name, sales: c.sales, revenue: c.revenue,
        }));
        const paymentMethodBreakdown = trim(data.paymentMethodBreakdown as any[], 8);
        const orderStatusBreakdown = trim(data.orderStatusBreakdown as any[], 8);
        const conversionFunnel = data.conversionFunnel || undefined; // {visits, addToCart, checkoutStarted, purchased}
        const customers = data.customers || undefined; // CustomerBreakdown {new, returning}
        // Bucket peak/quiet hours from hourlyVisits to keep payload tiny
        let hourlyHighlights: { peakHour?: number; peakVisits?: number; quietHour?: number; quietVisits?: number } | undefined;
        if (Array.isArray(data.hourlyVisits) && data.hourlyVisits.length > 0) {
          const arr = data.hourlyVisits as Array<{ hour: number; visits: number }>;
          const peak = arr.reduce((a, b) => (b.visits > a.visits ? b : a));
          const quiet = arr.reduce((a, b) => (b.visits < a.visits ? b : a));
          hourlyHighlights = {
            peakHour: peak.hour, peakVisits: peak.visits,
            quietHour: quiet.hour, quietVisits: quiet.visits,
          };
        }
        if (topProducts.length) compact.topProducts = topProducts;
        if (topCustomers.length) compact.topCustomers = topCustomers;
        if (trafficSources.length) compact.trafficSources = trafficSources;
        if (deviceBreakdown.length) compact.deviceBreakdown = deviceBreakdown;
        if (countryBreakdown.length) compact.countryBreakdown = countryBreakdown;
        if (topPages.length) compact.topPages = topPages;
        if (categoryBreakdown.length) compact.categoryBreakdown = categoryBreakdown;
        if (paymentMethodBreakdown.length) compact.paymentMethodBreakdown = paymentMethodBreakdown;
        if (orderStatusBreakdown.length) compact.orderStatusBreakdown = orderStatusBreakdown;
        if (conversionFunnel) compact.conversionFunnel = conversionFunnel;
        if (customers) compact.customers = customers;
        if (hourlyHighlights) compact.hourlyHighlights = hourlyHighlights;
        return { result: cap(JSON.stringify(compact)) };
      }

      case 'failed_searches': {
        const days = clampLimit(input.days, 30, 365);
        const limit = clampLimit(input.limit, 15, 50);
        const data = await analyticsApi.getSearchAnalytics(ctx.storeId, 'failed', limit, days);
        const compact = {
          windowDays: days,
          stats: data.stats,
          searches: (data.searches || []).slice(0, limit).map((s: any) => ({
            query: s.query,
            count: s.count,
            uniqueVisitors: s.uniqueVisitors,
          })),
        };
        return { result: cap(JSON.stringify(compact)) };
      }

      case 'abandoned_carts_summary': {
        const limit = clampLimit(input.limit, 10, 25);
        const data = await cartsApi.list(ctx.storeId, { limit });
        const sample = (data.carts || []).slice(0, limit).map((c: any) => ({
          id: c._id || c.cartId,
          total: c.total,
          currency: c.currency,
          items: Array.isArray(c.items) ? c.items.length : undefined,
          customer: c.customer?.name || c.clientInfo?.name || c.email || undefined,
          createdAt: c.createdAt,
          channel: c.recoveryChannel,
        }));
        return {
          result: cap(JSON.stringify({
            stats: data.stats,
            returned: sample.length,
            sample,
          })),
        };
      }

      case 'coupon_performance': {
        const limit = clampLimit(input.limit, 15, 50);
        const archived = input.archived === true;
        const data = await couponsApi.list(ctx.storeId, { limit, archived });
        const coupons = (data.coupons || []).slice(0, limit).map((c: any) => ({
          code: c.couponId,
          discountType: c.discountType,
          amount: c.amount,
          uses: c.usesCount ?? c.uses ?? 0,
          maxUses: c.maxUses,
          totalDiscount: c.totalDiscount,
          totalRevenue: c.totalRevenue,
          isActive: c.isActive,
          startDate: c.startDate,
          expiryDate: c.expiryDate,
        }));
        return {
          result: cap(JSON.stringify({ count: coupons.length, coupons })),
        };
      }

      case 'low_stock_alerts': {
        const filter = typeof input.filter === 'string' && ['low', 'out', 'all'].includes(input.filter)
          ? (input.filter as 'low' | 'out' | 'all')
          : 'low';
        const limit = clampLimit(input.limit, 20, 50);
        const data = await inventoryApi.list(ctx.storeId, { filter, limit });
        const items = (data.items || []).slice(0, limit).map((i: any) => ({
          productId: i.productId || i._id,
          title: i.title,
          quantity: i.quantity,
          sku: i.sku,
          price: i.price,
        }));
        return {
          result: cap(JSON.stringify({
            filter,
            stats: data.stats,
            returned: items.length,
            items,
          })),
        };
      }

      case 'navigate_to': {
        const route = String(input.route || '');
        const target = ROUTE_MAP[route];
        if (target === undefined) {
          return { result: `Unknown route "${route}". Valid: ${Object.keys(ROUTE_MAP).join(', ')}`, isError: true };
        }
        ctx.navigate(target ? `/${ctx.storeId}/${target}` : `/${ctx.storeId}`);
        return { result: `Navigated user to "${route}".` };
      }

      case 'open_product_create': {
        ctx.productForm.openCreate();
        return { result: 'Opened product-create modal. The user can now fill in title, price, variants, images, etc.' };
      }

      case 'open_product_edit': {
        const productId = String(input.productId || '');
        if (!productId) return { result: 'Missing productId', isError: true };
        ctx.productForm.openEdit(productId);
        return { result: `Opened product-edit modal for "${productId}". The user can change details, variants, images, etc.` };
      }

      case 'set_product_form_fields': {
        const handle = await waitForFormHandle(ctx);
        if (!handle) {
          return {
            result: 'Product form is not open. Call open_product_create or open_product_edit first.',
            isError: true,
          };
        }
        const allowedTypes = ['physical', 'digital', 'service', 'code', 'file'] as const;
        const allowedAccess = ['public', 'private', 'password', 'archived'] as const;
        const productType = typeof input.productType === 'string'
          && (allowedTypes as readonly string[]).includes(input.productType)
          ? (input.productType as typeof allowedTypes[number])
          : undefined;
        const accessibility = typeof input.accessibility === 'string'
          && (allowedAccess as readonly string[]).includes(input.accessibility)
          ? (input.accessibility as typeof allowedAccess[number])
          : undefined;
        handle.setFields({
          title: typeof input.title === 'string' ? input.title : undefined,
          description: typeof input.description === 'string' ? input.description : undefined,
          price: typeof input.price === 'number' ? input.price : undefined,
          salePrice: typeof input.salePrice === 'number' ? input.salePrice : undefined,
          compareAtPrice: typeof input.compareAtPrice === 'number' ? input.compareAtPrice : undefined,
          sku: typeof input.sku === 'string' ? input.sku : undefined,
          quantity: typeof input.quantity === 'number' ? input.quantity : undefined,
          unlimitedQuantity: typeof input.unlimitedQuantity === 'boolean' ? input.unlimitedQuantity : undefined,
          productType,
          accessibility,
          weight: typeof input.weight === 'number' ? input.weight : undefined,
        });
        const setKeys = Object.keys(input).filter((k) => input[k] !== undefined);
        return { result: `Set fields on the open product form: ${setKeys.join(', ') || 'none'}.` };
      }

      case 'add_product_variant_type': {
        const handle = await waitForFormHandle(ctx);
        if (!handle) {
          return { result: 'Product form is not open. Open it first.', isError: true };
        }
        const name = typeof input.name === 'string' ? input.name.trim() : '';
        const rawOptions = Array.isArray(input.options) ? input.options : [];
        const options = rawOptions
          .map((o) => (typeof o === 'string' ? o : ''))
          .map((s) => s.trim())
          .filter(Boolean);
        if (!name || options.length === 0) {
          return { result: 'name and at least one option are required.', isError: true };
        }
        const created = handle.addVariantType({ name, options });
        return {
          result: `Added variant axis "${created.name}" with ${created.options.length} option(s). Variants were auto-generated for every combination.`,
        };
      }

      case 'set_product_variant': {
        const handle = await waitForFormHandle(ctx);
        if (!handle) {
          return { result: 'Product form is not open. Open it first.', isError: true };
        }
        const rawCombo = input.combination;
        if (!rawCombo || typeof rawCombo !== 'object' || Array.isArray(rawCombo)) {
          return { result: 'combination object required (e.g. {"Color": "Red"})', isError: true };
        }
        const combination: Record<string, string> = {};
        for (const [k, v] of Object.entries(rawCombo as Record<string, unknown>)) {
          if (typeof k !== 'string' || typeof v !== 'string') continue;
          combination[k.trim()] = v.trim();
        }
        if (Object.keys(combination).length === 0) {
          return { result: 'combination cannot be empty', isError: true };
        }
        const matched = handle.setVariantOverride({
          combination,
          price: typeof input.price === 'number' ? input.price : undefined,
          priceModifier: typeof input.priceModifier === 'number' ? input.priceModifier : undefined,
          quantity: typeof input.quantity === 'number' ? input.quantity : undefined,
          sku: typeof input.sku === 'string' ? input.sku : undefined,
          isActive: typeof input.isActive === 'boolean' ? input.isActive : undefined,
        });
        if (!matched) {
          return {
            result: `No variant matches combination ${JSON.stringify(combination)}. Make sure all axes (and option names) are correct — call add_product_variant_type first if the axes don't exist yet.`,
            isError: true,
          };
        }
        const setKeys = Object.keys(input).filter((k) => k !== 'combination' && input[k] !== undefined);
        return { result: `Updated variant ${JSON.stringify(combination)}: ${setKeys.join(', ') || 'no fields'}.` };
      }

      case 'list_categories': {
        const data = await categoriesApi.list(ctx.storeId);
        const categories = ((data.categories as Array<Record<string, any>>) || []).map((c) => ({
          id: c._id || c.categoryId,
          name: c.name,
          parent: c.parent || null,
        }));
        return { result: cap(JSON.stringify({ count: categories.length, categories })) };
      }

      case 'get_store_settings': {
        const data = await settingsApi.get(ctx.storeId);
        const s = (data?.settings || {}) as Record<string, any>;
        // Normalize the typo'd field name (`soicalMediaLinks`) to a clean key
        // for the bot. The General settings tab writes the legacy array shape
        // [{app, link}] today, while older code paths use the object shape
        // {instagram: 'url'}. Handle both, otherwise the bot sees `{}` and
        // claims the merchant has no links even when they do.
        const socialLinksRaw = s.soicalMediaLinks ?? s.socialMediaLinks ?? null;
        let socialLinks: Record<string, string> = {};
        if (Array.isArray(socialLinksRaw)) {
          for (const item of socialLinksRaw) {
            if (item && typeof item.app === 'string' && typeof item.link === 'string' && item.link.trim()) {
              socialLinks[item.app] = item.link.trim();
            }
          }
        } else if (socialLinksRaw && typeof socialLinksRaw === 'object') {
          for (const [k, v] of Object.entries(socialLinksRaw as Record<string, unknown>)) {
            if (typeof v === 'string' && v.trim()) socialLinks[k] = v.trim();
          }
        }
        // Identify only which pixels are configured, not the actual ID values.
        const tracking = s.tracking || {};
        const pixelsConfigured = Object.fromEntries(
          ['facebookPixel', 'googleAnalytics', 'googleAds', 'tiktokPixel', 'snapchatPixel']
            .map((key) => [key, !!tracking[key]]),
        );
        // Payment methods: report only enabled flags (skip credentials).
        const pmRaw = (s.paymentMethods || {}) as Record<string, { enabled?: boolean } | undefined>;
        const paymentMethods = Object.entries(pmRaw)
          .filter(([, v]) => v && v.enabled)
          .map(([k]) => k);
        // Theme: surface the colors and key visual choices, drop the noise.
        const theme = s.theme || {};
        const themeCompact = {
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor,
          accentColor: theme.accentColor,
          textColor: theme.textColor,
          backgroundColor: theme.backgroundColor,
          buttonStyle: theme.buttonStyle,
          buttonRadius: theme.buttonRadius,
        };
        const seo = s.seo || {};
        const seoCompact = {
          metaTitle: seo.metaTitle,
          metaDescription: seo.metaDescription,
          twitterHandle: seo.twitterHandle,
          ogImageSet: !!seo.ogImage,
        };
        const compact = {
          name: s.name,
          description: typeof s.description === 'string' ? s.description.slice(0, 300) : undefined,
          currency: s.currency,
          language: s.language,
          enabledLanguages: s.enabledLanguages,
          defaultLanguage: s.defaultLanguage,
          timezone: s.timezone,
          customDomain: s.domain || s.customDomain,
          subdomain: s.subdomain,
          socialLinks,
          pixelsConfigured,
          paymentMethods,
          theme: themeCompact,
          seo: seoCompact,
        };
        return { result: cap(JSON.stringify(compact)) };
      }

      case 'add_product_category': {
        const handle = await waitForFormHandle(ctx);
        if (!handle) {
          return { result: 'Product form is not open. Open it first.', isError: true };
        }
        const categoryId = String(input.categoryId || '').trim();
        if (!categoryId) return { result: 'Missing categoryId', isError: true };
        handle.addCategory(categoryId);
        return { result: `Attached category "${categoryId}" to the open product form.` };
      }

      case 'open_coupon_create': {
        ctx.couponForm.openCreate();
        return { result: 'Opened coupon-create modal. Pair with set_coupon_form_fields to pre-fill.' };
      }

      case 'open_paylink_create': {
        ctx.paylinkForm.openCreate();
        return { result: 'Opened paylink-create modal. Pair with set_paylink_form_fields to pre-fill.' };
      }

      case 'set_paylink_form_fields': {
        const handle = await waitForHandle(ctx.paylinkForm.formRef);
        if (!handle) {
          return {
            result: 'Paylink form is not open. Call open_paylink_create first.',
            isError: true,
          };
        }
        handle.setFields({
          title: typeof input.title === 'string' ? input.title : undefined,
          amount: typeof input.amount === 'number' ? input.amount : undefined,
          currency: typeof input.currency === 'string' ? input.currency : undefined,
          description: typeof input.description === 'string' ? input.description : undefined,
        });
        const setKeys = Object.keys(input).filter((k) => input[k] !== undefined);
        return { result: `Set fields on the open paylink form: ${setKeys.join(', ') || 'none'}.` };
      }

      case 'set_coupon_form_fields': {
        const handle = await waitForHandle(ctx.couponForm.formRef);
        if (!handle) {
          return {
            result: 'Coupon form is not open. Call open_coupon_create first.',
            isError: true,
          };
        }
        const allowedTypes = ['percentage', 'fixed'] as const;
        const discountType = typeof input.discountType === 'string'
          && (allowedTypes as readonly string[]).includes(input.discountType)
          ? (input.discountType as typeof allowedTypes[number])
          : undefined;
        handle.setFields({
          couponId: typeof input.couponId === 'string' ? input.couponId : undefined,
          discountType,
          amount: typeof input.amount === 'number' ? input.amount : undefined,
          maximumAmount: typeof input.maximumAmount === 'number' ? input.maximumAmount : undefined,
          minPurchase: typeof input.minPurchase === 'number' ? input.minPurchase : undefined,
          maxUses: typeof input.maxUses === 'number' ? input.maxUses : undefined,
          startDate: typeof input.startDate === 'string' ? input.startDate : undefined,
          expiryDate: typeof input.expiryDate === 'string' ? input.expiryDate : undefined,
          description: typeof input.description === 'string' ? input.description : undefined,
          isActive: typeof input.isActive === 'boolean' ? input.isActive : undefined,
        });
        const setKeys = Object.keys(input).filter((k) => input[k] !== undefined);
        return { result: `Set fields on the open coupon form: ${setKeys.join(', ') || 'none'}.` };
      }

      case 'request_image_choice': {
        const result = await ctx.imagePicker.requestImageChoice({
          purpose: typeof input.purpose === 'string' ? input.purpose : undefined,
          hint: typeof input.hint === 'string' ? input.hint : undefined,
        });
        if (result.cancelled) {
          return { result: JSON.stringify({ cancelled: true }) };
        }
        return {
          result: JSON.stringify({
            cancelled: false,
            filename: result.filename,
            url: result.url,
            hash: result.hash,
            description: result.description,
            tags: result.tags,
            source: result.source,
          }),
        };
      }

      case 'list_page_sections': {
        const page = typeof input.page === 'string' && input.page.trim() ? input.page : 'home';
        const sections = await sectionsApi.list(ctx.storeId, page);
        const compact = sections.map((s) => ({
          name: s.name,
          variant: s.variant || 'default',
          order: s.order,
          hidden: s.hidden || false,
          inputs: s.inputs || {},
        }));
        return { result: cap(JSON.stringify({ page, count: compact.length, sections: compact })) };
      }

      case 'update_section_inputs': {
        const page = typeof input.page === 'string' && input.page.trim() ? input.page : 'home';
        const name = String(input.name || '');
        const order = Number(input.order);
        if (!name || !Number.isFinite(order)) {
          return { result: 'name and numeric order required', isError: true };
        }
        const sections = await sectionsApi.list(ctx.storeId, page);
        const idx = sections.findIndex((s) => s.name === name && s.order === order);
        if (idx < 0) return { result: `Section ${name}@${order} not found on page ${page}`, isError: true };
        const target = sections[idx];
        const partial = input.inputs && typeof input.inputs === 'object' ? input.inputs as Record<string, unknown> : {};
        target.inputs = { ...(target.inputs || {}), ...partial };
        if (typeof input.variant === 'string') target.variant = input.variant;
        if (typeof input.hidden === 'boolean') target.hidden = input.hidden;
        if (typeof input.className === 'string') target.className = input.className;
        await saveAndNormalize(ctx.storeId, page, sections);
        return { result: `Updated section ${name}@${order} on page ${page}.` };
      }

      case 'add_page_section': {
        const page = typeof input.page === 'string' && input.page.trim() ? input.page : 'home';
        const name = String(input.name || '');
        if (!name) return { result: 'name required', isError: true };
        const sections = await sectionsApi.list(ctx.storeId, page);
        const newSection: PageSection = {
          name,
          variant: typeof input.variant === 'string' ? input.variant : 'default',
          order: 0, // overwritten by saveAndNormalize
          inputs: input.inputs && typeof input.inputs === 'object' ? input.inputs as Record<string, unknown> : {},
        };
        const insertAt = typeof input.position === 'number' && Number.isFinite(input.position)
          ? Math.max(0, Math.min(Math.floor(input.position), sections.length))
          : sections.length;
        sections.splice(insertAt, 0, newSection);
        await saveAndNormalize(ctx.storeId, page, sections);
        return { result: `Added section "${name}" at position ${insertAt} on page ${page}.` };
      }

      case 'remove_page_section': {
        const page = typeof input.page === 'string' && input.page.trim() ? input.page : 'home';
        const name = String(input.name || '');
        const order = Number(input.order);
        if (!name || !Number.isFinite(order)) {
          return { result: 'name and numeric order required', isError: true };
        }
        const sections = await sectionsApi.list(ctx.storeId, page);
        const idx = sections.findIndex((s) => s.name === name && s.order === order);
        if (idx < 0) return { result: `Section ${name}@${order} not found on page ${page}`, isError: true };
        sections.splice(idx, 1);
        await saveAndNormalize(ctx.storeId, page, sections);
        return { result: `Removed section ${name}@${order} from page ${page}.` };
      }

      case 'move_section': {
        const page = typeof input.page === 'string' && input.page.trim() ? input.page : 'home';
        const name = String(input.name || '');
        const order = Number(input.order);
        const newPositionRaw = Number(input.newPosition);
        if (!name || !Number.isFinite(order) || !Number.isFinite(newPositionRaw)) {
          return { result: 'name, order, and newPosition required', isError: true };
        }
        const sections = await sectionsApi.list(ctx.storeId, page);
        const idx = sections.findIndex((s) => s.name === name && s.order === order);
        if (idx < 0) return { result: `Section ${name}@${order} not found on page ${page}`, isError: true };
        const [moved] = sections.splice(idx, 1);
        const clamped = Math.max(0, Math.min(Math.floor(newPositionRaw), sections.length));
        sections.splice(clamped, 0, moved);
        await saveAndNormalize(ctx.storeId, page, sections);
        return { result: `Moved ${name} from position ${order} to ${clamped} on page ${page}.` };
      }

      default:
        return { result: `Unknown tool: ${name}`, isError: true };
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { result: `Tool error: ${message}`, isError: true };
  }
}
